import { Utensils, ConciergeBell, Phone, Handshake, Users, PenLine, Music, GraduationCap, UserPlus, ShieldCheck } from "lucide-react";

export const SCENARIOS = [
  {
    id: "table",
    icon: Utensils,
    title: "Table Manners",
    blurb: "Family dinners, holidays and eating at a friend's house.",
    character: "Grandma",
    opening: { kid: "Dinner's ready, sweetie! Come sit down. Would you like some mashed potatoes?" },
    tips: [
      ["Wait for everyone", "Start eating when everyone is served, or when the cook says “Go ahead.”"],
      ["Napkin on your lap", "Use it to wipe your mouth and hands, not your sleeve."],
      ["“Please pass the…”", "Ask for things instead of reaching across the table."],
      ["Mouth closed", "Chew with your mouth closed and finish chewing before you talk."],
      ["Devices away", "Phones and tablets stay off the table so you can enjoy each other."],
      ["Thank the cook", "Say “Thank you for dinner” and ask “May I please be excused?”"],
    ],
  },
  {
    id: "ordering",
    icon: ConciergeBell,
    title: "Ordering Food",
    blurb: "Restaurants, cafés and drive-thrus. Order with kindness.",
    character: "Your server",
    opening: { kid: "Hi there, welcome in! What can I get for you today?" },
    tips: [
      ["Look and smile", "Look at the server, smile and say hello."],
      ["Ask, don't demand", "“May I please have…” or “I would like…, please” instead of “I want…”"],
      ["Questions are welcome", "“Could you please tell me what comes with that?”"],
      ["Kind fixes", "If something's wrong: “Excuse me, I'm sorry, but I ordered…”, said calmly."],
      ["Thank you, every time", "Say thank you when they take your order and when food arrives."],
      ["Be patient", "Servers work hard. Kind words make their day better."],
    ],
  },
  {
    id: "greetings",
    icon: Handshake,
    title: "Greetings & Handshakes",
    blurb: "Meeting someone new: eye contact, a firm handshake and a friendly hello.",
    character: "Mr. Lee",
    opening: {
      kid: "(The door opens) Well hello there! You must be my son's new friend. (holds out a hand to shake)",
    },
    tips: [
      ["Stand tall", "Strong body language: shoulders back, hands out of your pockets."],
      ["Eye contact", "Look them in the eyes. It shows respect and confidence."],
      ["Firm handshake", "Not squishy, not crushing. Grip the whole hand, two shakes, then let go."],
      ["Say your name", "“Hi, I'm ___. Nice to meet you.”"],
      ["Use their name", "“Nice to meet you, Mr. Lee.” People love hearing their name."],
      ["Be patient", "No interrupting. Let them finish, then answer in full sentences."],
      ["Kind goodbye", "“Thank you for having me. It was nice to meet you!”"],
    ],
  },
  {
    id: "thankyou",
    icon: PenLine,
    title: "Thank-You Notes",
    blurb: "Write a real thank-you note to a grandparent, teacher or friend.",
    character: "Grandma",
    multiline: true,
    printable: true,
    placeholder: "Dear ___,\n\nThank you so much for…\n\n…\n\nLove,\nYour name",
    opening: { kid: "" },
    variants: [
      { id: "grandparent", label: "Grandparent", character: "Grandma", opening: {
        kid: "Grandma sent you a birthday present! Write her a thank-you note below, then send it.", } },
      { id: "teacher", label: "Teacher", character: "Ms. Brooks", opening: {
        kid: "Your teacher, Ms. Brooks, helped you so much this year. Write her a thank-you note below.", } },
      { id: "friend", label: "Friend", character: "Riley", opening: {
        kid: "Your best friend Riley invited you to a sleepover. Write Riley a thank-you note below.", } },
    ],
    tips: [
      ["Start with a greeting", "“Dear Grandma,” or “Dear Ms. Brooks,”"],
      ["Name what you're thankful for", "Be specific: “Thank you for the art set,” not just “thanks for the gift.”"],
      ["Say why it matters", "How will you use it? How did it make you feel?"],
      ["Add a personal touch", "Share a memory or a bit of news: “I already drew a picture of your garden!”"],
      ["Thank them again", "“Thank you again for thinking of me.”"],
      ["Close and sign", "“Love,” for family and friends, “Sincerely,” for teachers, then your name."],
      ["Send it soon", "Within a week is best. Handwritten notes mean the most, so print it or copy it onto a card!"],
    ],
  },
  {
    id: "phone",
    icon: Phone,
    title: "Answering the Phone",
    blurb: "Greet callers, get a grown-up, take a message, and stay safe.",
    character: "Ms. Rivera (caller)",
    opening: { kid: "Ring, ring! The phone is ringing. Pick it up and say hello!" },
    tips: [
      ["Friendly hello", "“Hello, this is ___ speaking.”"],
      ["Speak clearly", "Talk slowly and clearly so the caller can understand you."],
      ["Who's calling?", "“May I please ask who's calling?”"],
      ["“One moment, please”", "Then walk to get your grown-up. No shouting across the house!"],
      ["Take a message", "Write down the name and number, then read it back to check."],
      ["Stay safe", "Never say you're home alone or share your address. Try: “They can't come to the phone right now. May I take a message?”"],
      ["Kind goodbye", "“Thank you for calling. Goodbye!”"],
    ],
  },
  {
    id: "music",
    icon: Music,
    title: "Asking for Music",
    blurb: "Practice asking a smart speaker to play a song, with please and thank you.",
    character: "Nova (smart speaker)",
    opening: { kid: "(Nova lights up) Hi there! I'm Nova, your music helper. What would you like to hear?" },
    tips: [
      ["Say the name first", "“Nova, …” so the speaker knows you're talking to it."],
      ["Ask with please", "“Nova, please play ‘Here Comes the Sun.’” or “Could you please play…?”"],
      ["Be clear", "Say the song and the artist slowly and clearly."],
      ["Polite changes", "“Could you please turn it down?” or “Next song, please.”"],
      ["Say thank you", "“Thank you, Nova!” Machines don't need it, but the habit builds kindness everywhere."],
    ],
  },
];

SCENARIOS.forEach((s) => { s.audience = "all"; });

SCENARIOS.push(
  {
    id: "friends",
    audience: "kid",
    icon: Users,
    title: "Manners with Friends",
    blurb: "Sharing, following house rules and cleaning up, at home and at a friend's house.",
    character: "Ava",
    opening: { kid: "(Ava opens the door) Hi! You're here! Come in! Want to play with my new art set? (holds up the markers)" },
    tips: [
      ["Share and take turns", "“You go first, then me.” Let your friend choose sometimes too."],
      ["Ask before you touch", "“May I please play with this?” It's their stuff and their house."],
      ["Follow their house rules", "Every family has different rules. Follow theirs, even if yours are different."],
      ["Stay with your friend", "Play together. Don't wander around their house by yourself."],
      ["Please and thank you", "Use them with your friend and your friend's grown-ups."],
      ["Clean up without being asked", "Before you leave, help put everything back, at their house and at yours."],
      ["Be ready to go", "When your grown-up comes, say goodbye, thank your friend's parent, and leave on time."],
    ],
  },
);

SCENARIOS.push(
  {
    id: "teachers",
    icon: GraduationCap,
    title: "Talking with Teachers",
    blurb: "Raise your hand, ask for help kindly, and thank your teacher.",
    character: "Ms. Brooks",
    audience: "school",
    opening: {
      kid: "(Ms. Brooks writes on the board) Okay class, open your math books to page 12. Does anyone have a question?",
    },
    tips: [
      ["Raise your hand", "Wait to be called on instead of shouting out."],
      ["“Excuse me, Ms. Brooks”", "If you need her, say excuse me and wait. No interrupting."],
      ["Ask for help kindly", "“Could you please explain that again?” is a smart, polite question."],
      ["Say sorry when needed", "“I'm sorry I was late” or “Sorry for interrupting.”"],
      ["Follow directions", "Listen the first time and try your best."],
      ["Thank your teacher", "“Thank you for helping me!” Teachers love to hear it."],
    ],
  },
  {
    id: "included",
    icon: UserPlus,
    title: "When Someone Is Left Out",
    blurb: "Notice who's alone and invite them in at recess, lunch or group work.",
    character: "Sophie",
    audience: "school",
    referral: true,
    opening: {
      kid: "(At recess, Sophie is sitting alone on the bench, watching everyone play)",
    },
    tips: [
      ["Notice", "Look around. Is someone sitting or playing alone?"],
      ["Walk over and smile", "A friendly face helps someone feel safe."],
      ["Say hi and your name", "“Hi, I'm ___. What's your name?”"],
      ["Invite them in", "“Want to play with us?” or “Do you want to sit with us?”"],
      ["Ask about them", "“What do you like to do?” Then really listen."],
      ["Keep including", "Save them a seat tomorrow, too. Kindness is a habit."],
    ],
  },
  {
    id: "standup",
    icon: ShieldCheck,
    title: "Standing Up Kindly",
    blurb: "What to do when you or a friend is teased, targeted or left out on purpose.",
    character: "Marcus",
    audience: "school",
    referral: true,
    opening: {
      kid: "(Marcus whispers) Did you see that? Some kids just took Leo's hat and are laughing at him. What should we do?",
    },
    tips: [
      ["Stay calm", "Take a deep breath. Your calm voice is powerful."],
      ["Say it clearly", "“Stop. That's not okay.” Short, calm and kind. Never tease back or fight."],
      ["Walk away together", "Go with your friend to a safe place."],
      ["Tell a trusted adult", "Telling a teacher or parent is not tattling. It's keeping people safe."],
      ["Check on your friend", "“Are you okay? Want to sit with me?”"],
      ["Online too", "Don't share or like mean posts. Save it and show a grown-up."],
      ["If it's happening to you", "It's not your fault. Tell a parent or teacher, every time."],
    ],
  },
);

export const findScenario = (id) => SCENARIOS.find((s) => s.id === id);
export const scenariosFor = (audience) => SCENARIOS.filter((s) => s.audience === audience);
